import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UploadCloud, Loader2, ArrowRight, Sparkles, Lock, ShieldCheck, Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export default function FreePreview() {
  const [email, setEmail] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const inputRef = useRef();

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a photo first"); return; }
    if (!consent) { toast.error("Please confirm you have permission to submit this photo"); return; }
    setBusy(true);
    setPreview(null);
    track.freePreviewStarted();
    try {
      const fd = new FormData();
      fd.append("email", email);
      fd.append("file", file);
      const { data } = await api.post("/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data.preview_image);
      setLeadId(data.lead_id);
      track.freePreviewCompleted();
      toast.success("Here's your free preview! We've also emailed it to you.");
    } catch (err) { track.freePreviewFailed(); toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 rounded-full px-3 py-1"><Sparkles className="w-3.5 h-3.5" /> Free preview</span>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slateink mt-4">See Your Photo as a Coloring Page</h1>
        <p className="text-slate-600 mt-4 text-lg">Upload one photo and get a free watermarked preview instantly — no payment required.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 items-start">
        <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-100 p-7 space-y-5">
          <div>
            <Label>Your photo</Label>
            <button type="button" onClick={() => inputRef.current?.click()} data-testid="preview-photo-input"
              className="mt-1.5 w-full border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center hover:border-amber transition-colors">
              <UploadCloud className="w-8 h-8 text-amber mx-auto" />
              <p className="text-sm text-slate-600 mt-2 font-medium">{file ? file.name : "Tap to choose a photo"}</p>
              <p className="text-xs text-slate-400 mt-1">JPG, PNG or HEIC — clear, well-lit, faces visible</p>
            </button>
            <input ref={inputRef} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} />
          </div>
          <div>
            <Label htmlFor="pemail">Email</Label>
            <Input id="pemail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="preview-email" className="mt-1.5" placeholder="you@example.com" />
            <p className="text-xs text-slate-400 mt-1.5">No account needed. We'll send tips and offers — unsubscribe anytime.</p>
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="preview-consent" className="mt-0.5" />
            <span className="text-xs text-slate-600">I confirm that I have permission to submit this image (and, for a child's photo, that I am the parent/legal guardian or have their permission) for creation of a personalized product.</span>
          </label>
          <div className="rounded-xl bg-sage/5 border border-sage/15 p-3 space-y-1.5">
            <p className="text-xs font-semibold text-sage flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Private &amp; secure</p>
            <p className="text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-sage" /> Your photo is never used for marketing without your permission.</p>
            <p className="text-xs text-slate-500 flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5 text-sage" /> Original uploads are automatically deleted per our retention policy.</p>
          </div>
          <Button type="submit" disabled={busy} data-testid="preview-submit" className="w-full rounded-full bg-amber hover:bg-amber-600 text-white font-semibold h-11">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating your preview…</> : "Get my free preview"}
          </Button>
        </form>

        <div className="bg-white rounded-3xl border border-slate-100 p-7 min-h-[320px] flex flex-col items-center justify-center text-center">
          {busy ? (
            <div><Loader2 className="w-10 h-10 animate-spin text-amber mx-auto" /><p className="text-slate-500 mt-4">Our studio is drawing your line art…</p></div>
          ) : preview ? (
            <div className="w-full" data-testid="preview-result">
              <img src={preview} alt="Your coloring page preview" className="w-full rounded-xl border border-slate-100" />
              <p className="font-display text-lg font-bold text-slateink mt-4">Love it?</p>
              <p className="text-sm text-slate-600">Turn your photo into a complete personalized coloring book.</p>
              <p className="text-xs text-sage mt-1.5 flex items-center justify-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> A copy of this preview is on its way to your inbox.</p>
              <div className="flex justify-center gap-2 mt-3 text-xs">
                <span className="bg-subtle rounded-lg px-2.5 py-1 font-medium">10 pages · $29</span>
                <span className="bg-subtle rounded-lg px-2.5 py-1 font-medium">20 pages · $49</span>
                <span className="bg-subtle rounded-lg px-2.5 py-1 font-medium">30 pages · $69</span>
              </div>
              <Button asChild className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold mt-4"><Link to={`/product/personalized-coloring-book${leadId ? `?preview=${leadId}` : ""}`} data-testid="preview-create-book">Create my book <ArrowRight className="w-4 h-4 ml-1" /></Link></Button>
            </div>
          ) : (
            <div className="text-slate-400"><Sparkles className="w-10 h-10 mx-auto mb-3 text-amber/40" /><p>Your preview will appear here.</p></div>
          )}
        </div>
      </div>
    </div>
  );
}
